/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Ref, Button, Dropdown, Segment } from "semantic-ui-react";
import { m11useChanged, selectCurrentUserApiToken, selectCurrentUserM11Use } from "../../slices/currentUser/currentUserSlice";
import { changeTool } from "../../slices/footer/footerSlice";
import { m11ResearchStudyFoiChanged } from "../../slices/m11StepCount/m11StepCountSlice";
import submitFhirJson from "../../functions/submitFhirJsonAPI";
import axiosInstance from "../../functions/axiosInstance";
import { handleRememberScroll, restoreScrollPosition } from "../../functions/eventHandlers";
import AlertModal from "../../components/AlertModal";
import StringEntry from "../../datatypeComponents/StringEntry";
import server_config from "../../ServerConfiguration";

const fevir_node_server_address = server_config["fevir_node_server_address"];

const setM11useToSample = async (apiToken = null) => {
    if (!apiToken) {
        console.log("no apiToken");
        return null;
    }
    console.log("calling server with setm11usetosample");
    return await axiosInstance({
        method: 'POST',
        url: fevir_node_server_address,
        timeout: 120000,
        data: {
            'functionid': 'setm11usetosample',
            'apiToken': apiToken
        },
    }).then((resp) => {
        if (resp?.data) {
            if (resp.data.success) {
                return resp.data;
                //resp.data should be {success: true}
            }
            console.log("failed response from setm11usetosample call");
            return null;
        }
        console.log("NO RESPONSE from setm11usetosample call");
    }).catch((error) => { console.log(error); });
};

const setM11useToPaid = async (apiToken = null) => {
    if (!apiToken) {
        console.log("no apiToken");
        return null;
    }
    console.log("calling server with setm11usetopaid");
    return await axiosInstance({
        method: 'POST',
        url: fevir_node_server_address,
        timeout: 120000,
        data: {
            'functionid': 'setm11usetopaid',
            'apiToken': apiToken
        },
    }).then((resp) => {
        if (resp?.data) {
            if (resp.data.success) {
                return resp.data;
                //resp.data should be {success: true}
            }
            console.log("failed response from setm11usetopaid call");
            return null;
        }
        console.log("NO RESPONSE from setm11usetopaid call");
    }).catch((error) => { console.log(error); });
};

const createM11ResearchStudy = async (title, dispatch, apiToken, setAlertModalState, setFoiEntryState, navigate) => {
    let m11ResearchStudy = {
        "resourceType": "ResearchStudy",
        "meta": { "profile": ["http://hl7.org/fhir/uv/clinical-study-protocol/StructureDefinition/m11-research-study-profile"] },
        "title": title,
        "status": "active"
    };
    let response = await submitFhirJson(m11ResearchStudy, title, apiToken);
    let resourceFOI;
    if (response?.success) {
        resourceFOI = response.formstateid;
        dispatch(m11ResearchStudyFoiChanged(resourceFOI));
        setFoiEntryState({ "foi": parseInt(resourceFOI), "title": title });
        navigate("/m11report/" + resourceFOI + "/step/1");
    } else {
        setAlertModalState(prevState => {
            return {
                ...prevState,
                "open": true,
                "header": "Error",
                "content": "Problem creating M11ResearchStudy Resources."
            };
        });
        return null;
    }
}

const getUserM11ResearchStudies = async (apiToken, setUserM11ResearchStudies, setUserM11ReportOptions) => {
    await axiosInstance({
        method: 'POST',
        url: fevir_node_server_address,
        timeout: 120000,
        data: {
            'functionid': 'getuserm11researchstudies',
            'apiToken': apiToken
        },
    }).then((resp) => {
        if (resp?.data) {
            if (resp.data.success && resp.data.m11ResearchStudies?.length) {
                setUserM11ResearchStudies(resp.data.m11ResearchStudies); //Do we actually need this one?
                const userM11ReportOptions = resp.data.m11ResearchStudies.map((entry) => { return { key: entry.id, text: entry.title, value: entry.id }; });
                setUserM11ReportOptions(userM11ReportOptions);
                //return resp.data;
                //resp.data should be {success: true, m11ResearchStudies: [{id: FOI, title: Resource Title}]}
            }
            console.log("failed response from getuserm11researchstudies call");
            return null;
        }
        console.log("NO RESPONSE from getuserm11researchstudies call");
    }).catch((error) => { console.log(error); });

}

// eslint-disable-next-line no-empty-pattern
const M11ReportPage = ({ }) => {
    const apiToken = useSelector(selectCurrentUserApiToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const m11User = useSelector(selectCurrentUserM11Use);
    const scrollTimerRef = useRef(null);

    const [userM11ResearchStudies, setUserM11ResearchStudies] = useState(null); //We don't actually need this one
    const [userM11ReportOptions, setUserM11ReportOptions] = useState(null);

    const [foiEntryState, setFoiEntryState] = useState({ "foi": "", "title": "" });
    const [alertModalState, setAlertModalState] = useState({
        open: false, alertHistory: [], header: "", content: ""
    });

    const segment_scroll_storage_key = 'm11_start_scroll';

    const setScrollRef = useCallback((nodeElement) => {
        restoreScrollPosition(nodeElement, segment_scroll_storage_key, 0);
    }, []);

    useEffect(() => {
        if (apiToken) {
            getUserM11ResearchStudies(apiToken, setUserM11ResearchStudies, setUserM11ReportOptions);
        }
    }, [apiToken]);

    useEffect(() => {
        dispatch(changeTool("Computable Publishing®: M11 Report Authoring Tool"));
    }, []);

    return <Ref innerRef={(nodeElement) => { setScrollRef(nodeElement); }}>
        <Segment
            className={`containerSegment maxRemainderOfPageSegment`}
            onScroll={(e) => { handleRememberScroll(e, segment_scroll_storage_key, scrollTimerRef, 140); }}
            onClick={(e) => {
                e.stopPropagation(); // If the user clicks inside then it doesn't trigger the onClick of the parent, which is meant to make the whitespace outside clickable only
            }}
        >
            <AlertModal alertModalState={alertModalState} setAlertModalState={setAlertModalState} />
            <div>
                {m11User && <div>
                    <h2>Select or Create M11ResearchStudy Resource to Use the Tool</h2>
                    {userM11ReportOptions?.length &&
                        <div>
                            <h3>Select an M11ResearchStudy</h3>
                            <p>If you already have an M11ResearchStudy Resource you will use to create your M11 Report, select it here:</p>                   <Dropdown
                                name="m11researchstudieselection"
                                placeholder="Select M11ResearchStudy"
                                closeOnChange selection clearable
                                search={true}
                                selectOnBlur={false}
                                autoComplete='off'
                                style={{ width: "800px" }}
                                options={userM11ReportOptions}
                                value={foiEntryState.foi}
                                onChange={(e, data) => {
                                    setFoiEntryState(prevState => { return { ...prevState, foi: data.value }; });
                                }} />

                            <Button className="formButton" content="Launch M11 Report Authoring Tool"
                                positive
                                onClick={() => {
                                    dispatch(m11ResearchStudyFoiChanged(String(foiEntryState.foi)));
                                    navigate("/m11report/" + String(foiEntryState.foi) + "/step/1");
                                }}
                                disabled={!foiEntryState.foi}
                            />
                        </div>
                    }
                    <h3>{userM11ReportOptions?.length ? "Or c" : "C"}reate a new M11ResearchStudy Resource by entering a title for your research study:</h3>
                    <StringEntry elementName='title'
                        fieldLabel='Title for M11ResearchStudy'
                        startingValue={foiEntryState.title}
                        setResourceState={setFoiEntryState} />
                    <Button
                        className="formButton"
                        content="Launch M11 Report Authoring Tool"
                        positive
                        style={{ marginBottom: "16px" }}
                        onClick={() => {
                            createM11ResearchStudy(foiEntryState.title, dispatch, apiToken, setAlertModalState, setFoiEntryState, navigate);
                        }}
                        disabled={!foiEntryState.title} />
                </div>}
                {m11User === "sample" && <div>
                    <h2>You will be using the M11 Report Authoring Tool (Test Drive Version)</h2>
                    <p>The M11 Report Authoring Tool (Test Drive Version) will take you through a sampling of 19 of the 45 steps to create a complete M11 Report.</p>
                    <p>With the M11 Report Authoring Tool (Test Drive Version) you will be able to create an M11 Report with the Title Page, Section 1 Protocol Summary, Section 2 Introduction, Section 3.1.1 Primary Objective 1, Section 5 Trial Population, and Section 14 References, and then see how an M11 Report would be created with these sections completed.</p>      <Button className="formButton positive"
                        content="Use the Full M11 Report Authoring tool"
                        compact
                        onClick={() => {
                            setAlertModalState(prevState => {
                                return {
                                    ...prevState,
                                    "open": true,
                                    "header": "Use the M11 Report Authoring Tool (Professional Version)",
                                    "content": "Email support@computablepublishing.com with a request to use the M11 Report Authoring Tool."
                                };
                            });
                            setM11useToPaid(apiToken);
                        }} />
                </div>}
                <h2>Stop Writing Protocols in Unstructured Word Documents. Author the Future of Clinical Trials.</h2>
                <p>Welcome to the <b>Computable Publishing<sup>®</sup>: M11 Report Authoring Tool</b>: a deterministic, structured data-entry wizard designed to operationalize the upcoming <b>ICH M11 digital template</b> using native <b>HL7® FHIR® R6</b> normative-bound resources.</p>
                <p>Built by the developers of the Evidence Based Medicine on FHIR (EBMonFHIR) Implementation Guide.</p>
                {!m11User &&
                    <Button className="formButton positive"
                        content="Try the M11 Report Authoring Tool (Test Drive Version) Free"
                        compact
                        onClick={() => {
                            if (apiToken) {
                                setM11useToSample(apiToken);
                                dispatch(m11useChanged("sample"));
                            } else {
                                alert("Please log in to use this function.");
                            }
                        }} />
                }
                <p>HL7<sup>&#174;</sup> and FHIR<sup>&#174;</sup> are the registered trademarks of Health Level Seven International and their use of these trademarks does not constitute an endorsement by HL7.</p>
                <h2>Why FHIR R6 and ICH M11 Matters Now</h2>
                <p>Regulatory bodies like the European Medicines Agency (EMA) are moving rapidly toward computable, data-driven protocols. Traditional text processors isolate your clinical definitions, requiring expensive, manual downstream programming to build EDCs and submit registries.</p>
                <p>Our M11 Report Authoring Tool bridges the gap. It provides a guided, multi-user wizard that forces structural data integrity at the moment of creation—producing pristine M11 PDFs alongside compliant FHIR JSON payloads for the digigal protocol.</p>
                <h2>Core Platform Features</h2>
                <p><b>Rule-Based Data Wizard:</b> Zero generative AI risk. Input titles, objectives (ResearchStudy) and endpoints (EvidenceVariable), and study eligibility criteria criteria (Group) into an intentional schema that guarantees data integrity.</p>
                <p><b>Full Registry Compatibility:</b> Built on the exact architectures mapping to ClinicalTrials.gov, EU CTIS, and the CDISC USDM.</p>
                <p><b>Collaborative Multi-User Authoring:</b> Track content state changes, ownership attribution, and historical archives natively on the platform.</p>
                <p><b>Universal Export Pipeline:</b> Instantly download formatted M11 PDFs or structured JSON data ready for downstream clinical operations.</p>
                <br />
                {!m11User &&
                    <Button className="formButton positive" content="Try the M11 Report Authoring Tool (Test Drive Version) Free" compact
                        onClick={() => {
                            if (apiToken) {
                                setM11useToSample(apiToken);
                                dispatch(m11useChanged("sample"));
                            } else {
                                alert("Please log in to use this function.");
                            }
                        }} />
                }
            </div>
        </Segment>
    </Ref>
};

export default M11ReportPage;
export { setM11useToPaid };